import { makeGatewayCallPromise } from "./dependencies/logincheck.api.es6";
import { ME_SESSION_STORAGE_KEYS } from "./dependencies/mesessionstoragekeys.es6";
import { logIfDebugEnabled, DEBUG_SESSION_KEY } from "./dependencies/utils.es6";
import MD5 from "./dependencies/md5HashGenerator.es6";
import {
  initDocmsListener,
  buildConsentMap,
  clearDataUsageScopes,
  pushConsentTrackingEvent,
} from "./docms-global-listener.js";

const LANDING_PAGE_PARAMETER = "ONEDOC_SPA"; // OneWeb_registration

const ONE_DAY_IN_MS = 24 * 60 * 60 * 1000;
const LOGIN_FLOW_EVENT = "login_flow";
const UPDATE_CONSENT_EVENT = "update_consent";

let loginCheckParameters;

function _getConsentSignature(response) {
  if (typeof response === "string") {
    try {
      response = JSON.parse(response);
    } catch (parseErr) {
      return null;
    }
  }

  if (!response || typeof response !== "object") {
    return null;
  }

  const consent = buildConsentMap(response.consentStatus);
  const sortedConsent = Object.keys(consent)
    .sort()
    .reduce(function (sorted, scopeName) {
      sorted[scopeName] = consent[scopeName];
      return sorted;
    }, {});

  return JSON.stringify({
    consentStatus: sortedConsent,
    user: {
      firstName: response.firstName || "",
      lastName: response.lastName || "",
      email: response.email || "",
      phoneNumber: response.phoneNumber || "",
    },
  });
}

let _fetchDataUsageScopes = function (loginCheckParameter = {}) {
    const { b2xLoginData, bffURL } = loginCheckParameter;

    var country = b2xLoginData?.country;
    var token = b2xLoginData?.authState?.jwe;
    const body = _createRequestBody(country);

    logIfDebugEnabled(
      "[LOGGED-IN FLOW] BFF request:",
      JSON.stringify({
        bffURL: bffURL,
        body: body,
      }),
    );

    return makeGatewayCallPromise(bffURL, token, body);
  },
  _createRequestBody = function (countryCode) {
    var dataScopesSecretHash = "123456";
    const body = {
      searchContext: {
        system: LANDING_PAGE_PARAMETER,
      },
      purpose: "Direct Marketing",
      crossCustomerTypeSearchAllowed: true,
      customerSearchData: {
        customerType: "Person",
      },
      hashCode: MD5(
        [
          countryCode, // market
          LANDING_PAGE_PARAMETER, // system
          "", // companyName1
          "", // companyName2
          "", // externalKeyIssuer
          "", // externalKeyValue
          dataScopesSecretHash,
        ].join(";"),
      ),
    };

    return JSON.stringify(body);
  },
  _writeDataUsageScopesToStorage = function (
    response = {},
    eventLabel = LOGIN_FLOW_EVENT,
  ) {
    const previous = window.sessionStorage.getItem(
      ME_SESSION_STORAGE_KEYS.dataUsageScopes,
    );
    window.sessionStorage.removeItem(
      ME_SESSION_STORAGE_KEYS.dataUsageScopesTimeStampFailed,
    );

    const { customerConsentState, customerFound, ciasUserData } = response;

    logIfDebugEnabled(
      "[LOGGED-IN FLOW] Raw BFF customerConsentState:",
      JSON.stringify({
        customerFound: customerFound,
        customerConsentState: customerConsentState && {
          enableDataUsage: customerConsentState.enableDataUsage,
          dataUsageScopes: customerConsentState.dataUsageScopes,
          usages: customerConsentState.usages,
          ecosystemAgreedDateGlobal:
            customerConsentState.ecosystemAgreedDateGlobal,
          ecosystemAgreedDatesMarket:
            customerConsentState.ecosystemAgreedDatesMarket,
        },
      }),
    );

    if (customerConsentState) {
      const { enableDataUsage } = customerConsentState;
      window.sessionStorage.setItem(
        ME_SESSION_STORAGE_KEYS.declarationOfConsent,
        JSON.stringify(enableDataUsage),
      );
    }

    if (customerConsentState && customerFound) {
      const { dataUsageScopes } = customerConsentState;
      if (dataUsageScopes) {
        var mergedConsentStatus = {
          ecosystem: false,
          epost: false,
          phone: false,
          post: false,
        };
        var hasEcosystemUsage = false;
        var usages = Array.isArray(customerConsentState.usages)
          ? customerConsentState.usages
          : [];

        usages.forEach(function (scope) {
          if (scope && scope.name) {
            var usageName = String(scope.name).toLowerCase();
            mergedConsentStatus[usageName] = !!scope.agreed;
            if (usageName === "ecosystem") {
              hasEcosystemUsage = true;
            }
          }
        });

        if (!hasEcosystemUsage) {
          var hasEcosystemDate =
            customerConsentState.ecosystemAgreedDateGlobal !== null &&
            customerConsentState.ecosystemAgreedDateGlobal !== undefined &&
            customerConsentState.ecosystemAgreedDateGlobal !== "";
          var ecosystemAgreedDatesMarket =
            customerConsentState.ecosystemAgreedDatesMarket;

          if (!hasEcosystemDate && Array.isArray(ecosystemAgreedDatesMarket)) {
            hasEcosystemDate = ecosystemAgreedDatesMarket.some(
              function (marketConsent) {
                return (
                  marketConsent &&
                  marketConsent.ecosystemAgreedDate !== null &&
                  marketConsent.ecosystemAgreedDate !== undefined &&
                  marketConsent.ecosystemAgreedDate !== ""
                );
              },
            );
          }

          if (hasEcosystemDate) {
            mergedConsentStatus.ecosystem = true;
          }
        }

        dataUsageScopes.forEach(function (scope) {
          if (scope && scope.name) {
            mergedConsentStatus[String(scope.name).toLowerCase()] =
              !!scope.agreed;
          }
        });

        logIfDebugEnabled(
          "[LOGGED-IN FLOW] Merged consent map:",
          JSON.stringify(mergedConsentStatus),
        );

        const fullResponse = {
          callbackType: "bffResponse",
          result: "BFF_RESPONSE",
          resultMessage: "",
          consentStatus: mergedConsentStatus,
          firstName: ciasUserData?.given_name ?? "",
          lastName: ciasUserData?.family_name ?? "",
          email: ciasUserData?.email ?? "",
          phoneNumber: ciasUserData?.phone_number ?? "",
        };

        logIfDebugEnabled(
          "[LOGGED-IN FLOW] Full response mapped:",
          JSON.stringify(fullResponse),
        );
        window.sessionStorage.setItem(
          ME_SESSION_STORAGE_KEYS.dataUsageScopes,
          JSON.stringify(fullResponse),
        );
        logIfDebugEnabled(
          "[LOGGED-IN FLOW] Write dataUsageScopes time stamp in MS to session storage",
        );
        window.sessionStorage.setItem(
          ME_SESSION_STORAGE_KEYS.dataUsageScopesTimeStamp,
          JSON.stringify(Date.now()),
        );
        if (
          _getConsentSignature(previous) !== _getConsentSignature(fullResponse)
        ) {
          logIfDebugEnabled(
            "[LOGGED-IN FLOW] Forwarding stored consent state to datalayer",
          );
          pushConsentTrackingEvent(fullResponse, eventLabel);
        } else {
          logIfDebugEnabled(
            "[LOGGED-IN FLOW] Consent state unchanged — skipping login_flow datalayer push",
          );
        }
      }
    }
  },
  _writeFalseDataUsageScopesToStorage = function (
    eventLabel = LOGIN_FLOW_EVENT,
  ) {
    window.sessionStorage.removeItem(
      ME_SESSION_STORAGE_KEYS.dataUsageScopesTimeStamp,
    );
    window.sessionStorage.setItem(
      ME_SESSION_STORAGE_KEYS.dataUsageScopesTimeStampFailed,
      JSON.stringify(Date.now()),
    );

    var existing = window.sessionStorage.getItem(
      ME_SESSION_STORAGE_KEYS.dataUsageScopes,
    );
    if (existing) {
      try {
        var parsed = JSON.parse(existing);
        var consentStatus = parsed && parsed.consentStatus;

        if (Array.isArray(parsed)) {
          consentStatus = parsed;
          parsed = {
            callbackType: "bffError",
            result: "",
            resultMessage: "",
            consentStatus: consentStatus,
          };
        }

        if (consentStatus && typeof consentStatus === "object") {
          parsed.callbackType = "bffError";
          parsed.consentStatus = Array.isArray(consentStatus)
            ? consentStatus.reduce(function (consent, scope) {
                if (scope && scope.name) {
                  consent[String(scope.name).toLowerCase()] = false;
                }
                return consent;
              }, {})
            : Object.keys(consentStatus).reduce(function (consent, scopeName) {
                consent[String(scopeName).toLowerCase()] = false;
                return consent;
              }, {});
          window.sessionStorage.setItem(
            ME_SESSION_STORAGE_KEYS.dataUsageScopes,
            JSON.stringify(parsed),
          );
          if (_getConsentSignature(existing) !== _getConsentSignature(parsed)) {
            logIfDebugEnabled(
              "[LOGGED-IN FLOW] Forwarding degraded consent state to datalayer",
            );
            pushConsentTrackingEvent(parsed, eventLabel);
          } else {
            logIfDebugEnabled(
              "[LOGGED-IN FLOW] Consent state unchanged — skipping login_flow datalayer push",
            );
          }
        }
      } catch (parseErr) {
        window.sessionStorage.setItem(
          ME_SESSION_STORAGE_KEYS.dataUsageScopes,
          existing.replaceAll("true", "false"),
        );
      }
    }
  },
  _getTimePassed = function (timeStamp) {
    return Date.now() - parseInt(timeStamp);
  },
  _getLoginIdentity = function (loginCheckParameter) {
    const b2xLoginData =
      (loginCheckParameter && loginCheckParameter.b2xLoginData) || {};
    const authState = b2xLoginData.authState || {};
    const ciamId = b2xLoginData.ciamId || b2xLoginData.ciam || authState.ciamId;

    if (ciamId) {
      return String(ciamId);
    }

    return authState.jwe ? MD5(authState.jwe) : "";
  },
  _makeDataUsageScopesRequestAndValidateResponse = function (
    loginCheckParameter,
    eventLabel = LOGIN_FLOW_EVENT,
  ) {
    _fetchDataUsageScopes(loginCheckParameter)
      .then((dataUsage) => {
        logIfDebugEnabled(
          "[LOGGED-IN FLOW] Raw BFF response:",
          JSON.stringify(dataUsage),
        );
        _writeDataUsageScopesToStorage(dataUsage, eventLabel);
      })
      .catch((error) => {
        var errorDetails =
          error &&
          (error.status !== undefined || error.statusText !== undefined)
            ? JSON.stringify({
                status: error.status,
                statusText: error.statusText,
              })
            : error && error.message
              ? error.message
              : String(error);
        logIfDebugEnabled(
          "[LOGGED-IN FLOW] Raw BFF response failure:",
          errorDetails,
        );
        _writeFalseDataUsageScopesToStorage(eventLabel);
      });
  };

/**
 * Re-fetch the latest consent data straight from DoCMS (through the same
 * logged-in BFF retrieval flow used on login) and write it to
 * sessionStorage/dataLayer via the existing write functions.
 * No-ops if the login flow never ran for this page (e.g. guest user),
 * since there are no cached login parameters (bffURL/jwt/market) to call
 * the BFF with.
 */
export function refreshConsentData() {
  if (!loginCheckParameters) {
    logIfDebugEnabled(
      "[LOGGED-IN FLOW] Skipping consent refresh — no cached login parameters",
    );
    return;
  }
  _makeDataUsageScopesRequestAndValidateResponse(
    loginCheckParameters,
    UPDATE_CONSENT_EVENT,
  );
}

export default function declarationOfConsent(loginData) {
  window.sessionStorage.setItem(DEBUG_SESSION_KEY, true);

  logIfDebugEnabled("Start loading", loginData);
  logIfDebugEnabled(
    "[LOGGED-IN FLOW] declarationOfConsent triggered — user is logged in",
  );

  // Ensure Global OneDoC Listener is active (idempotent — already initialized at module load)
  initDocmsListener();

  loginCheckParameters = loginData || {};

  const currentIdentity = _getLoginIdentity(loginCheckParameters);
  const storedIdentity = window.sessionStorage.getItem(
    ME_SESSION_STORAGE_KEYS.dataUsageScopesIdentity,
  );

  if (currentIdentity && currentIdentity !== storedIdentity) {
    logIfDebugEnabled(
      "[LOGGED-IN FLOW] Invalidating cached dataUsageScopes because of a new or changed login",
    );
    clearDataUsageScopes();
    window.sessionStorage.setItem(
      ME_SESSION_STORAGE_KEYS.dataUsageScopesIdentity,
      currentIdentity,
    );
    logIfDebugEnabled(
      "[LOGGED-IN FLOW] Making BFF API call to fetch dataUsageScopes",
    );
    _makeDataUsageScopesRequestAndValidateResponse(loginCheckParameters);
    return;
  }

  const dataUsageScopesTimeStampFailed = window.sessionStorage.getItem(
    ME_SESSION_STORAGE_KEYS.dataUsageScopesTimeStampFailed,
  );

  // In case request to BFF failed do retry in 1 day
  if (dataUsageScopesTimeStampFailed) {
    const timePassedMs = _getTimePassed(dataUsageScopesTimeStampFailed);

    if (timePassedMs < ONE_DAY_IN_MS) {
      logIfDebugEnabled(
        "Next retry will be in: " +
          Math.round((ONE_DAY_IN_MS - timePassedMs) / (60 * 1000)) +
          " minutes",
      );
      return;
    }
  }

  logIfDebugEnabled(
    "[LOGGED-IN FLOW] Making BFF API call to fetch dataUsageScopes",
  );
  _makeDataUsageScopesRequestAndValidateResponse(loginCheckParameters);
}
